// Sprint A: Multi-resource per class/course dialog (admin + teacher).
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { FolderPlus, Plus, Trash2, Youtube, FileText, Link as LinkIcon } from "lucide-react";

const inp = "w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF0066]/40";

const TYPES = [
  { value: "youtube", label: "YouTube link", icon: Youtube, color: "rose" },
  { value: "vimeo", label: "Vimeo / Video", icon: Youtube, color: "purple" },
  { value: "pdf", label: "PDF (URL)", icon: FileText, color: "amber" },
  { value: "doc", label: "DOC / DOCX", icon: FileText, color: "blue" },
  { value: "link", label: "Web link", icon: LinkIcon, color: "fuchsia" },
];

const CATEGORIES = [
  { value: "notes", label: "Notes" },
  { value: "practice", label: "Practice sheet" },
  { value: "video", label: "Video / Recording" },
  { value: "assignment", label: "Assignment" },
  { value: "tool", label: "External tool" },
];

export function ClassResourcesButton({ classId, classTitle, onDone }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ type: "youtube", url: "", title: "", description: "", category: "notes" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/classes/${classId}/resources`); setItems(data); }
    catch (e) { toast.error("Failed to load"); }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, classId]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/admin/classes/${classId}/resources`, f);
      toast.success("Added");
      setF({ type: "youtube", url: "", title: "", description: "", category: "notes" });
      load(); onDone?.();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this resource?")) return;
    try { await api.delete(`/admin/resources/${id}`); toast.success("Deleted"); load(); onDone?.(); }
    catch { toast.error("Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-ghost !py-1.5 text-xs" title="Resources (multi PDF/YouTube)" data-testid={`resources-${classId}`}>
          <FolderPlus className="w-3.5 h-3.5" /> Resources{items.length > 0 ? ` (${items.length})` : ""}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#121212] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><FolderPlus className="w-5 h-5 text-[#FF0066]" /> Class resources</DialogTitle>
          <div className="text-xs text-white/50">{classTitle} — multiple PDFs, YouTube links, practice sheets, etc.</div>
        </DialogHeader>

        <form onSubmit={add} className="space-y-3 rounded-lg bg-white/[0.03] border border-white/10 p-3">
          <div className="text-[11px] uppercase tracking-wider text-[#FF0066] font-bold">➕ Add new resource</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs text-white/50 mb-1">Type</div>
              <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className={inp}>
                {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="text-xs text-white/50 mb-1">Category</div>
              <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={inp}>
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <div className="text-xs text-white/50 mb-1">URL</div>
            <input required value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} className={inp} placeholder="https://youtu.be/… or https://cdn.example.com/file.pdf" data-testid="resource-url" />
          </label>
          <label className="block">
            <div className="text-xs text-white/50 mb-1">Title</div>
            <input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inp} placeholder="Lecture 1 — Notes PDF" data-testid="resource-title" />
          </label>
          <label className="block">
            <div className="text-xs text-white/50 mb-1">Description (optional)</div>
            <textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={inp} />
          </label>
          <button disabled={busy} className="bg-[#FF0066] hover:bg-[#e6005c] text-white font-semibold rounded-full px-4 py-2 text-sm inline-flex items-center gap-1 disabled:opacity-60" data-testid="add-resource-btn">
            <Plus className="w-4 h-4" /> {busy ? "Adding…" : "Add resource"}
          </button>
        </form>

        <div className="mt-4 space-y-2">
          <div className="text-xs text-white/50 mb-1">Current resources ({items.length})</div>
          {items.length === 0 && <div className="text-xs text-white/40 px-3 py-4 border border-dashed border-white/10 rounded-lg text-center">No resources yet. Add multiple PDFs, YouTube links, practice sheets above.</div>}
          {items.map((r) => {
            const T = TYPES.find((x) => x.value === r.type) || TYPES[4];
            return (
              <div key={r.id} className="rounded-lg border border-white/10 bg-white/[0.02] p-2.5 flex items-start gap-2" data-testid={`resource-row-${r.id}`}>
                <T.icon className={`w-4 h-4 text-${T.color}-300 shrink-0 mt-0.5`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{r.title}</div>
                  <div className="text-[10px] text-white/40 mt-0.5">{r.category} • {new Date(r.created_at).toLocaleDateString()}</div>
                  <a href={r.url} target="_blank" rel="noreferrer" className="text-[11px] text-[#FF0066] hover:underline truncate block">{r.url}</a>
                </div>
                <button onClick={() => del(r.id)} className="p-1.5 rounded hover:bg-red-500/10 text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ClassResourcesButton;
